// src/pages/auth/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  Avatar,
  Fade,
  Slide,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  AdminPanelSettings,
  School,
  Security,
  Login as LoginIcon,
  Block,
  Warning
} from '@mui/icons-material';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, db } from '../../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Tema personalizado con paleta verde pastel
const pastelGreenTheme = createTheme({
  palette: {
    primary: {
      main: '#81C784',
      light: '#A5D6A7',
      dark: '#66BB6A',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#C8E6C9',
      light: '#E8F5E8',
      dark: '#A5D6A7',
      contrastText: '#2E7D32',
    },
    background: {
      default: '#F1F8E9',
      paper: '#FFFFFF',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
    },
    info: {
      main: '#26A69A',
      light: '#80CBC4',
    },
    warning: {
      main: '#FFA726',
      light: '#FFB74D',
    },
    error: {
      main: '#EF5350',
      light: '#E57373',
    },
    text: {
      primary: '#1B5E20',
      secondary: '#388E3C',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(129, 199, 132, 0.15)',
          borderRadius: '16px',
          border: '1px solid #E8F5E8',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          fontSize: '1rem',
        },
        contained: {
          boxShadow: '0 4px 16px rgba(129, 199, 132, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(129, 199, 132, 0.4)',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(5px)',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#81C784',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#66BB6A',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          '&.MuiAlert-standardError': {
            backgroundColor: 'rgba(239, 83, 80, 0.1)',
            border: '1px solid rgba(239, 83, 80, 0.3)',
          },
          '&.MuiAlert-standardWarning': {
            backgroundColor: 'rgba(255, 167, 38, 0.1)',
            border: '1px solid rgba(255, 167, 38, 0.3)',
          },
        },
      },
    },
  },
});

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [verifyingRole, setVerifyingRole] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // ✅ Función para verificar el rol del usuario y determinar el acceso
  const checkUserRole = async (user) => {
    try {
      console.log('🔍 Verificando rol para usuario:', user.uid);

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        console.log('❌ Documento del usuario no encontrado');
        return {
          hasAccess: false,
          isAdmin: false,
          reason: 'No se encontró el perfil del usuario en el sistema'
        };
      }

      const userData = userDoc.data();
      console.log('📄 Datos del usuario:', userData);

      // Verificar el rol del usuario (soporte para diferentes campos)
      const userRole = userData.role || userData.rol;

      // Verificar si el usuario está activo
      if (userData.active === false || userData.status === 'inactive') {
        return {
          hasAccess: false,
          isAdmin: false,
          reason: 'Tu cuenta está deshabilitada'
        };
      }

      // Lista de roles que pueden acceder al panel de administración
      const adminRoles = ['admin', 'administrator', 'superadmin', 'manager'];
      const isAdmin = userData.isAdmin === true || (userRole && adminRoles.includes(userRole.toString().toLowerCase()));

      console.log(`🎭 Rol del usuario: ${userRole}, Es admin: ${isAdmin}`);

      // Si no tiene rol definido o no es admin, es usuario regular
      if (!userRole || !isAdmin) {
        console.log('👤 Usuario regular identificado');
        return {
          hasAccess: true,
          isAdmin: false,
          userData: userData,
          userType: 'user'
        };
      }

      console.log('✅ Usuario administrador verificado');
      return {
        hasAccess: true,
        isAdmin: true,
        userData: userData,
        userType: 'admin'
      };

    } catch (error) {
      console.error('❌ Error verificando rol:', error);
      return {
        hasAccess: false,
        isAdmin: false,
        reason: 'Error al verificar permisos. Inténtalo de nuevo'
      };
    }
  };

  // Validación de email en tiempo real
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('El email es requerido');
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError('Formato de email inválido');
      return false;
    } else {
      setEmailError('');
      return true;
    }
  };

  // Validación de contraseña
  const validatePassword = (password) => {
    if (!password) {
      setPasswordError('La contraseña es requerida');
      return false;
    } else if (password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validar campos
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    
    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);

    try {
      // 1. Autenticar al usuario en Firebase
      console.log('🔐 Intentando autenticar usuario...');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ Usuario autenticado correctamente:', user.email);
      
      // 2. Verificar el rol del usuario
      setVerifyingRole(true);
      console.log('🔍 Verificando permisos del usuario...');

      const userCheck = await checkUserRole(user);

      if (!userCheck.hasAccess) {
        // El usuario no tiene acceso - cerrar sesión y mostrar error
        console.log('❌ Acceso denegado:', userCheck.reason);

        // Cerrar la sesión inmediatamente
        await signOut(auth);

        setError(userCheck.reason);
        setVerifyingRole(false);
        setLoading(false);
        return;
      }

      // 3. Usuario verificado - guardar datos y navegar al portal
      console.log('✅ Acceso autorizado - Redirigiendo al portal');
      console.log('📊 Datos del usuario:', userCheck.userData);

      // Guardar datos del usuario en localStorage (tanto admin como usuario regular)
      if (userCheck.userData) {
        const userData = {
          uid: user.uid,
          email: user.email,
          role: userCheck.userData.role || userCheck.userData.rol,
          name: `${userCheck.userData.firstName || ''} ${userCheck.userData.lastName || ''}`.trim(),
          lastLogin: new Date().toISOString(),
          userType: userCheck.isAdmin ? 'admin' : 'user',
          isAdmin: userCheck.isAdmin
        };

        localStorage.setItem('userData', JSON.stringify(userData));

        // También guardar en adminData si es admin (para compatibilidad)
        if (userCheck.isAdmin) {
          localStorage.setItem('adminData', JSON.stringify(userData));
        }
      }

      // Redirigir segun el rol del usuario
      if (userCheck.isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/portal', { replace: true });
      }
      
    } catch (error) {
      console.error('❌ Error en el proceso de login:', error);
      
      let errorMessage = 'Error al iniciar sesión';
      
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          errorMessage = 'Email o contraseña incorrectos';
          break;
        case 'auth/invalid-email':
          errorMessage = 'El formato del correo electrónico no es válido';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Demasiados intentos fallidos. Por favor, inténtalo más tarde';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Esta cuenta ha sido deshabilitada';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Error de conexión. Verifica tu conexión a internet';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Credenciales inválidas. Verifica tu email y contraseña';
          break;
        default:
          errorMessage = 'Error de autenticación. Intenta nuevamente';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setVerifyingRole(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Determinar el estado de carga y el mensaje
  const getLoadingMessage = () => {
    if (verifyingRole) {
      return 'Verificando permisos...';
    }
    if (loading) {
      return 'Verificando credenciales...';
    }
    return 'Iniciar Sesión';
  };

  const isFormLoading = loading || verifyingRole;

  return (
    <ThemeProvider theme={pastelGreenTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #F1F8E9 0%, #E8F5E8 50%, #C8E6C9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Fade in={mounted} timeout={800}>
            <Card
              elevation={0}
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '24px',
                border: '1px solid rgba(129, 199, 132, 0.2)',
                overflow: 'hidden',
              }}
            >
              {/* Header con gradiente */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #81C784 0%, #66BB6A 100%)',
                  py: 4,
                  px: 3,
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                  },
                }}
              >
                <Slide direction="down" in={mounted} timeout={600}>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'primary.main',
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      }}
                    >
                      <AdminPanelSettings sx={{ fontSize: 40 }} />
                    </Avatar>
                    
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        mb: 1,
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      Portal AMPA del Bosc
                    </Typography>
                    
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <School sx={{ fontSize: 20 }} />
                      AMPA del Bosc
                    </Typography>
                  </Box>
                </Slide>
              </Box>

              <CardContent sx={{ p: 4 }}>
                <Slide direction="up" in={mounted} timeout={800}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <Security color="primary" />
                      <Typography variant="h6" color="text.primary" fontWeight={600}>
                        Acceso Seguro al Portal
                      </Typography>
                    </Box>

                    {/* Mensaje de estado durante verificación de rol */}
                    {verifyingRole && (
                      <Fade in={verifyingRole}>
                        <Alert 
                          severity="info" 
                          sx={{ mb: 3 }}
                          icon={<Security />}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            Verificando permisos...
                          </Typography>
                          <Typography variant="caption" display="block">
                            Validando tu acceso al sistema AMPA
                          </Typography>
                        </Alert>
                      </Fade>
                    )}

                    {error && (
                      <Fade in={!!error}>
                        <Alert 
                          severity={error.includes('rol') || error.includes('permisos') ? 'warning' : 'error'}
                          sx={{ mb: 3 }}
                          icon={error.includes('rol') || error.includes('permisos') ? <Block /> : <Warning />}
                          onClose={() => setError('')}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {error.includes('rol') || error.includes('permisos') ? 'Acceso Denegado' : 'Error de Autenticación'}
                          </Typography>
                          <Typography variant="body2">
                            {error}
                          </Typography>
                          {error.includes('rol') && (
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              💡 Si crees que esto es un error, contacta con el administrador principal del sistema.
                            </Typography>
                          )}
                        </Alert>
                      </Fade>
                    )}
                    
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Correo electrónico"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) validateEmail(e.target.value);
                        }}
                        onBlur={() => validateEmail(email)}
                        error={!!emailError}
                        helperText={emailError}
                        disabled={isFormLoading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="primary" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />
                      
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (passwordError) validatePassword(e.target.value);
                        }}
                        onBlur={() => validatePassword(password)}
                        error={!!passwordError}
                        helperText={passwordError}
                        disabled={isFormLoading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="primary" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleTogglePassword}
                                  edge="end"
                                  color="primary"
                                  disabled={isFormLoading}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 3 }}
                      />
                      
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isFormLoading || !email || !password}
                        startIcon={isFormLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                        sx={{
                          mb: 3,
                          py: 1.5,
                          fontSize: '1.1rem',
                          background: isFormLoading ? undefined : 'linear-gradient(135deg, #81C784 0%, #66BB6A 100%)',
                        }}
                      >
                        {getLoadingMessage()}
                      </Button>

                      <Divider sx={{ my: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          ¿Necesitas ayuda?
                        </Typography>
                      </Divider>
                      
                      <Grid container spacing={2} justifyContent="center">
                        <Grid item>
                          <Link
                            component={RouterLink}
                            to="/forgot-password"
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            ¿Olvidaste tu contraseña?
                          </Link>
                        </Grid>
                        <Grid item>
                          <Typography variant="body2" color="text.secondary" component="span">
                            •
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Link
                            component={RouterLink}
                            to="/register"
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Crear cuenta nueva
                          </Link>
                        </Grid>
                      </Grid>

                      {/* Información de seguridad */}
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 3,
                          p: 2,
                          backgroundColor: 'rgba(129, 199, 132, 0.1)',
                          border: '1px solid rgba(129, 199, 132, 0.2)',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block">
                          🔒 Acceso seguro para miembros de la comunidad AMPA del Bosc
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          🛡️ Todas las sesiones están cifradas y monitoreadas por seguridad
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          👥 Acceso diferenciado según tu rol en la comunidad
                        </Typography>
                      </Paper>

                      {/* Legal Links */}
                      <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Al iniciar sesión, acepta nuestros términos y políticas:
                        </Typography>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 2,
                          flexWrap: 'wrap',
                          '& a': {
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }
                        }}>
                          <Link
                            component="a"
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Política de Privacidad
                          </Link>
                          <Link
                            component="a"
                            href="/terms-of-service"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Términos de Uso
                          </Link>
                          <Link
                            component="a"
                            href="/cookies-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Cookies
                          </Link>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Slide>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Login;